import React, { useState, useRef, useEffect, memo, useMemo } from 'react';
import { Pressable, View, Text } from 'react-native';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import AnimatedLottieView from '@utils/lottieAnimation';
import styles from '@stylesheet';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useTranslation } from 'react-i18next';
import * as Animatable from 'react-native-animatable';
import LottieView from 'lottie-react-native';

// ErrorBoundary component
const SecondaryMenu = ({ item, index }) => {
  const page = useSelector((state: DefaultRootState) => state.navigation.page);
  const { t } = useTranslation();
  const [buttonState, setButtonState] = useState(false);
  const navigation = useNavigation();
  const hoveredStyle = styles.menuHovered;
  const unMarkedStyle = styles.menuItemSelected;
  const markedStyle = styles.menuItem;
  const secondaryItemRef = useRef<LottieView | null>(null);
  const [iconSource, setIconSource] = useState(null);

  useEffect(()=>{
    return () => {
      if (secondaryItemRef.current) {
        secondaryItemRef.current.reset();
        secondaryItemRef.current = null;
      }
    }
  },[])

  const confirmedScreen = () => {
    try {
      if (page.params?.screen === item.screen && item.parent === page.name) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  };

  const getItemStyle = (hovered: boolean) => {
    const isActive = confirmedScreen();

    if (isActive) {
      // Active item - use marked style with hover effect if hovered
      return hovered ? [markedStyle, hoveredStyle, { top: 30 }] : [markedStyle, { top: 30 }];
    } else {
      // Inactive item - use unmarked style with hover effect if hovered
      return hovered ? [unMarkedStyle, hoveredStyle] : unMarkedStyle;
    }
  };

  const handlePress = () => {
    if (secondaryItemRef.current) {
      secondaryItemRef.current.reset();
      secondaryItemRef.current = null;
    }

    try {
      // Use navigation.navigate with proper nested navigation structure
      if (item.parent) {
        (navigation as any).navigate(item.parent, { screen: item.screen });
      } else if (item.primaryScreen) {
        (navigation as any).navigate(item.primaryScreen, { screen: item.screen });
      } else {
        // Fallback to direct navigation
        (navigation as any).navigate(item.screen);
      }
    } catch (error) {
      console.error("Navigation error in SecondaryMenu:", error);
      // Fallback navigation
      (navigation as any).navigate(item.screen || item.parent);
    }
  };

  return useMemo(()=> (
    <Animatable.View useNativeDriver animation={'slideInLeft'} duration={400} delay={index * 50} style={{ flex: 1, overflow: "visible", justifyContent: "center", alignContent: "center", alignItems: "center", left: -4 }}>
      <Pressable
        style={[{ flex: 1, overflow:"visible", alignSelf: 'stretch', minWidth: 60, paddingTop: 5, paddingVertical: 2 }]}
        hitSlop={10}
        onPress={handlePress}
        onHoverIn={()=>secondaryItemRef.current?.play()}
      >
        {({ hovered }: any) => (
          <Animatable.View useNativeDriver duration={20} transition={'backgroundColor'} style={[getItemStyle(hovered), { flexDirection: 'column', flex: 1, alignSelf: 'stretch', overflow: 'visible', paddingVertical: 5, top: -10, gap: 2 }]}>
          <View style={{ flex: 1, backgroundColor: 'transparent', alignItems: 'center', overflow: 'visible', minHeight: 50 }}>
            {/* Render the Lottie animation */}
            <AnimatedLottieView
              source={iconSource}
              ref={secondaryItemRef}
                style={{ flex: 1, width: 40, height: 40, top: 5, transform: [{ scale: item.scale ? item.scale : 1 }] }}
              speed={0.5}
              autoPlay={false}
              loop={false}
              key={iconSource}
            />
          </View>
            <Text adjustsFontSizeToFit numberOfLines={2} selectable={false} style={[styles.menuItemText, { maxWidth: 60, flexWrap: "wrap", userSelect: "none", pointerEvents: "none", minWidth:80, fontSize: 12, paddingHorizontal: 5, textAlign: 'center', flex: 1, overflow:"visible" }]}>
            {t(item.header).toString()}
          </Text>
          </Animatable.View>
        )}
      </Pressable>
    </Animatable.View>
  ), [item, index, page, iconSource, buttonState]);
};

export default memo(SecondaryMenu);
